import { useCallback, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import {
  Avatar,
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Tag,
  Typography,
  Upload,
  theme,
} from "antd";
import type { UploadFile } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserGroup,
  faPlus,
  faUpload,
  faTrash,
  faFileArrowDown,
  faFilm,
  faImage,
  faFile,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { useAuth } from "../../contexts/auth";
import { useToasts } from "../../contexts/toasts";
import { Roles } from "../../enums/Roles";
import {
  Mentoring,
  MentoringItem,
  MentoringPartner,
  MentoringFileType,
} from "../../api/Mentoring";
import "./Mentoring.css";

dayjs.locale("it");

const { Title, Text, Paragraph } = Typography;

const initials = (nome?: string, cognome?: string) =>
  `${(nome?.[0] || "").toUpperCase()}${(cognome?.[0] || "").toUpperCase()}` || "?";

const typeIcon = (t: MentoringFileType) =>
  t === "video" ? faFilm : t === "image" ? faImage : t === "document" ? faFile : faFile;

export default function MentoringPage() {
  const { loggedUser } = useAuth();
  const { showToast } = useToasts();
  const { token } = theme.useToken();
  const [form] = Form.useForm();

  const isManager =
    loggedUser?.role === Roles.Superadmin || loggedUser?.role === Roles.Trainer;

  const [loadingPartners, setLoadingPartners] = useState(true);
  const [partners, setPartners] = useState<MentoringPartner[]>([]);
  const [active, setActive] = useState<MentoringPartner | null>(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [items, setItems] = useState<MentoringItem[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [saving, setSaving] = useState(false);

  const loadPartners = useCallback(async () => {
    setLoadingPartners(true);
    try {
      const res = await Mentoring.getPartners();
      const list = res.data.list ?? [];
      setPartners(list);
      // Auto-select the first partner (an athlete typically has just their trainer).
      if (list.length && !active) setActive(list[0]);
    } catch (_) {
      showToast("mentoring-partners-err", "Errore nel caricamento dei contatti", "", "danger");
    } finally {
      setLoadingPartners(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  const loadItems = useCallback(
    async (partnerId: number) => {
      setLoadingItems(true);
      try {
        const res = await Mentoring.getItems(partnerId);
        setItems(res.data.list ?? []);
      } catch (_) {
        showToast("mentoring-items-err", "Errore nel caricamento dei file", "", "danger");
      } finally {
        setLoadingItems(false);
      }
    },
    [showToast]
  );

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  useEffect(() => {
    if (active) loadItems(active.id);
  }, [active, loadItems]);

  const openUpload = () => {
    form.resetFields();
    setFileList([]);
    setModalOpen(true);
  };

  const submit = async () => {
    if (!active) return;
    try {
      const values = await form.validateFields();
      const file = fileList[0]?.originFileObj;
      if (!file) {
        showToast("mentoring-nofile", "Seleziona un file", "", "warning");
        return;
      }
      setSaving(true);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", values.title);
      if (values.description) fd.append("description", values.description);
      if (values.tags?.length) fd.append("tags", JSON.stringify(values.tags));
      await Mentoring.createItem(active.id, fd);
      showToast("mentoring-new", "File condiviso", "", "success");
      setModalOpen(false);
      loadItems(active.id);
    } catch (err: any) {
      if (err?.errorFields) return;
      showToast("mentoring-save-err", "Errore nella condivisione del file", "", "danger");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item: MentoringItem) => {
    try {
      await Mentoring.deleteItem(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      showToast("mentoring-del", "Elemento eliminato", "", "success");
    } catch (_) {
      showToast("mentoring-del-err", "Errore nell'eliminazione", "", "danger");
    }
  };

  const canDelete = (item: MentoringItem) =>
    item.ownerId === loggedUser?.id ||
    (isManager && item.trainerId === loggedUser?.id);

  const renderPreview = (item: MentoringItem) => {
    if (!item.signedUrl) return null;
    if (item.fileType === "image") {
      return (
        <img
          src={item.signedUrl}
          alt={item.title}
          style={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: "8px 8px 0 0" }}
        />
      );
    }
    if (item.fileType === "video") {
      return (
        <video
          src={item.signedUrl}
          controls
          preload="metadata"
          style={{ width: "100%", maxHeight: 220, background: "#000", borderRadius: "8px 8px 0 0" }}
        />
      );
    }
    return (
      <Flex
        align="center"
        justify="center"
        vertical
        gap={8}
        style={{ height: 140, background: token.colorFillSecondary, borderRadius: "8px 8px 0 0" }}
      >
        <FontAwesomeIcon icon={typeIcon(item.fileType)} style={{ fontSize: 34, color: token.colorTextTertiary }} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {item.originalName || "Documento"}
        </Text>
      </Flex>
    );
  };

  return (
    <Container fluid="lg" className="my-3">
      <Helmet>
        <title>Mentoring</title>
      </Helmet>

      <Title level={2} className="mb-0">
        <FontAwesomeIcon icon={faUserGroup} className="me-2" style={{ color: token.colorPrimary }} />
        Mentoring
      </Title>
      <Text type="secondary">
        {isManager
          ? "Condividi video, schede e file con i tuoi atleti."
          : "Scambia file e materiali con il tuo trainer."}
      </Text>

      <Row gutter={[16, 16]} className="mt-3">
        {/* Partner list */}
        <Col xs={24} md={8} lg={7}>
          <Card title="Contatti" styles={{ body: { padding: 8 } }}>
            {loadingPartners ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : partners.length === 0 ? (
              <Empty description="Nessun contatto" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                dataSource={partners}
                renderItem={(p) => (
                  <List.Item
                    className={`mentoring-partner ${active?.id === p.id ? "active" : ""}`}
                    onClick={() => setActive(p)}
                    style={{ cursor: "pointer", padding: "10px 12px", borderRadius: 8 }}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar style={{ backgroundColor: token.colorPrimary }}>
                          {initials(p.nome, p.cognome)}
                        </Avatar>
                      }
                      title={`${p.nome} ${p.cognome}`}
                      description={<Text type="secondary" style={{ fontSize: 12 }}>{p.email}</Text>}
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {/* Session items */}
        <Col xs={24} md={16} lg={17}>
          {!active ? (
            <Card>
              <Empty description="Seleziona un contatto per vedere i file condivisi" />
            </Card>
          ) : (
            <section className="mentoring-session">
              <Flex
                justify="space-between"
                align="center"
                wrap="wrap"
                gap={12}
                className="mentoring-session-header"
              >
                <Flex align="center" gap={10}>
                  <Avatar size="large" style={{ backgroundColor: token.colorPrimary }}>
                    {initials(active.nome, active.cognome)}
                  </Avatar>
                  <div>
                    <strong style={{ fontSize: 16 }}>
                      {active.nome} {active.cognome}
                    </strong>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {items.length} element{items.length === 1 ? "o" : "i"} condivis
                        {items.length === 1 ? "o" : "i"}
                      </Text>
                    </div>
                  </div>
                </Flex>
                <Button
                  className="cm-btn cm-btn--primary"
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={openUpload}
                >
                  Condividi
                </Button>
              </Flex>

              {loadingItems ? (
                <Skeleton active paragraph={{ rows: 4 }} />
              ) : items.length === 0 ? (
                <Empty
                  className="mentoring-session-empty"
                  description="Nessun file condiviso in questa sessione"
                />
              ) : (
                <Row gutter={[16, 16]}>
                  {items.map((item) => (
                    <Col xs={24} sm={12} lg={8} key={item.id}>
                      <Card
                        size="small"
                        className="h-100 mentoring-item-card"
                        cover={renderPreview(item)}
                        styles={{ body: { display: "flex", flexDirection: "column" } }}
                      >
                        <Flex justify="space-between" align="flex-start" gap={6}>
                          <strong>{item.title}</strong>
                          {canDelete(item) && (
                            <Popconfirm
                              title="Eliminare l'elemento?"
                              okText="Elimina"
                              okButtonProps={{ danger: true }}
                              cancelText="Annulla"
                              onConfirm={() => remove(item)}
                            >
                              <Button type="text" size="small" danger icon={<FontAwesomeIcon icon={faTrash} />} />
                            </Popconfirm>
                          )}
                        </Flex>

                        {item.description && (
                          <Paragraph type="secondary" ellipsis={{ rows: 2 }} className="mb-1" style={{ fontSize: 12 }}>
                            {item.description}
                          </Paragraph>
                        )}

                        {item.tags && item.tags.length > 0 && (
                          <Flex gap={4} wrap="wrap" className="mb-1">
                            {item.tags.map((tag) => (
                              <Tag key={tag} color="blue" style={{ margin: 0 }}>
                                {tag}
                              </Tag>
                            ))}
                          </Flex>
                        )}

                        <Flex justify="space-between" align="center" className="mt-auto pt-2">
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {item.owner ? `${item.owner.nome} ${item.owner.cognome}` : ""}
                            {item.createdAt ? ` · ${dayjs(item.createdAt).format("D MMM")}` : ""}
                          </Text>
                          {item.signedUrl && (
                            <a href={item.signedUrl} target="_blank" rel="noopener noreferrer" download>
                              <FontAwesomeIcon icon={item.fileType === "document" ? faFileArrowDown : faDownload} />
                            </a>
                          )}
                        </Flex>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </section>
          )}
        </Col>
      </Row>

      <Modal
        title="Condividi un file"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        okText="Condividi"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" className="mt-3">
          <Form.Item name="title" label="Titolo" rules={[{ required: true, message: "Il titolo è obbligatorio" }]}>
            <Input placeholder="Es. Scheda gennaio, Video squat…" />
          </Form.Item>
          <Form.Item name="description" label="Descrizione">
            <Input.TextArea rows={3} placeholder="Descrizione (opzionale)" />
          </Form.Item>
          <Form.Item name="tags" label="Tag">
            <Select mode="tags" placeholder="Aggiungi tag e premi invio" tokenSeparators={[","]} />
          </Form.Item>
          <Form.Item label="File" required>
            <Upload
              maxCount={1}
              beforeUpload={() => false}
              fileList={fileList}
              onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
            >
              <Button icon={<FontAwesomeIcon icon={faUpload} />}>Seleziona file</Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Immagini, video o documenti (pdf, excel, ppt…).
            </Text>
          </Form.Item>
        </Form>
      </Modal>
    </Container>
  );
}
